import {EditorView} from 'prosemirror-view';
import {EditorState, Selection} from 'prosemirror-state';
import {history} from 'prosemirror-history';
import {keymap} from 'prosemirror-keymap';
import {baseKeymap} from 'prosemirror-commands';
import {gapCursor} from 'prosemirror-gapcursor';
import {tableEditing} from 'prosemirror-tables';

import {buildInputRules} from './buildInputRules';
import {menuTooltip} from './plugins/menuTooltip';
import {trailingNode} from './plugins/trailingNode';
import {emojiSuggestions} from './plugins/emojiSuggestions';
import {tableTooltip} from './plugins/tableTooltip';
import {buildKeymaps} from './buildKeymaps';
import {atCommandTrigger, commandSuggestions, slashCommandTrigger} from './plugins/commandSuggestions';
import {Schema} from 'prosemirror-model';

import 'prosemirror-view/style/prosemirror.css';
import 'prosemirror-gapcursor/style/gapcursor.css';
import './styles/editor.css';

import {editorBlur} from './plugins/editorBlur';
import {buildAtCommands, buildCommands} from './buildCommands';
import {nodeCommands, nodeInputRules, nodeKeyMaps, nodeParsers, nodeSchemaSpecs, nodeSerializers,} from './nodes';
import {markInputRules, markKeyMaps, markParsers, markSchemaSpecs, markSerializers,} from './marks';
import {markdownSerializer} from './markdownSerializer';
import {fileDropHandler} from './plugins/fileDropHandler';
import {documentNode} from './markdownParser';
import {markViews, nodeViews} from './views';
import {headingAnchor} from './plugins/headingAnchor';
import {gridColumnResizing} from './plugins/gridColumnResizing';
import {cardContentHeader} from './plugins/cardContentHeader';
import {borderNode} from './plugins/borderNode';
import {dropCursor} from "prosemirror-dropcursor";
import {pageSuggestions} from "./plugins/pageSuggestions";

const documentSerializer = markdownSerializer(nodeSerializers, markSerializers);

export default class MarkdownEditor {
    constructor({target, doc, editable = true, fileUploadUrl, onEditorBlur}) {
        const schema = new Schema({
            nodes: nodeSchemaSpecs,
            marks: markSchemaSpecs
        });

        this.view = new EditorView(target, {
            state: EditorState.create({
                schema,
                doc: documentNode(schema, doc, nodeParsers, markParsers),
            }),
            nodeViews: nodeViews,
            markViews: markViews,
            editable: () => editable
        });

        const plugins = [];
        if (editable) {
            plugins.push(
                buildInputRules(this.view.state, nodeInputRules(this.view.state.schema), markInputRules(this.view.state.schema)),
                trailingNode,
                emojiSuggestions,
                pageSuggestions,
                commandSuggestions(
                    slashCommandTrigger(),
                    buildCommands(this.view, nodeCommands(this.view), fileUploadUrl)),
                commandSuggestions(
                    atCommandTrigger(),
                    buildAtCommands(this.view)),
                ...buildKeymaps(this.view.state, nodeKeyMaps(this.view.state.schema), markKeyMaps(this.view.state.schema)),
                keymap(baseKeymap),
                menuTooltip,
                fileDropHandler(fileUploadUrl),
                headingAnchor,
                cardContentHeader,
                dropCursor({
                    width: 3,
                    color: '#1AB6FF',
                }),
                gapCursor(),
                history(),
                tableEditing(),
                tableTooltip(transaction => this.view.dispatch(transaction)),
                gridColumnResizing({
                    dispatch: transaction => this.view.dispatch(transaction)
                }),
                borderNode
            );

            if (onEditorBlur) {
                plugins.push(editorBlur(view => onEditorBlur(documentSerializer.serialize(view.state.doc))));
            }
        }

        this.view.updateState(
            this.view.state.reconfigure({
                plugins
            })
        );

        this.focusAtStart();
    }

    set height(height) {
        this.view.dom.style.minHeight = `${height}px`;
    }

    focusAtStart() {
        const selection = Selection.atStart(this.view.state.doc);
        const transaction = this.view.state.tr.setSelection(selection);
        this.view.dispatch(transaction);
        this.view.focus();
    }

    focusAtEnd() {
        const selection = Selection.atEnd(this.view.state.doc);
        const transaction = this.view.state.tr.setSelection(selection);
        this.view.dispatch(transaction);
        this.view.focus();
    }

    destroy() {
        document.querySelectorAll('#menu-tooltip, #command-tooltip, #emoji-tooltip, #page-search-tooltip, #menu_link, #menu_table_color').forEach(el => el.remove());
        this.view && this.view.destroy();
    }
}
